import { useState } from 'react'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { CircleAlert, Plus, RefreshCw } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  useV1PollsAdminCapabilitiesRetrieve,
  useV1PollsAdminPollsList,
} from '@/api/client'
import { ActivationTabs } from '@/components/activation/activation-tabs'
import { PageHeader } from '@/components/page-header'
import { Pager } from '@/components/pager'
import { keepPreviousPage } from '@/lib/keep-previous-page'
import { envelopeCount, envelopeResults } from '@/lib/page-envelope'
import { pollCapabilitiesOrClosed } from '@/components/push-poll/poll-api'
import { canAuthorPolls, canViewPolls } from '@/components/push-poll/poll-capabilities'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export const Route = createFileRoute('/_panel/zadania/ankiety/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: PollListPage,
})

const PAGE_SIZE = 25

function PollListPage() {
  const [page, setPage] = useState(1)
  const navigate = useNavigate()
  const capabilitiesQuery = useV1PollsAdminCapabilitiesRetrieve()
  const capabilities = pollCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canView = canViewPolls(capabilities)
  const canAuthor = canAuthorPolls(capabilities)
  const listQuery = useV1PollsAdminPollsList(
    { page, page_size: PAGE_SIZE },
    {
      query: {
        enabled: canView,
        placeholderData: keepPreviousPage({ page, page_size: PAGE_SIZE }),
      },
    },
  )
  const listData = listQuery.data?.status === 200 ? listQuery.data.data : undefined
  const polls = envelopeResults(listData)
  const totalCount = envelopeCount(listData)
  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  // Po usunięciu ostatniego wiersza bieżąca strona przestaje istnieć.
  if (listData && !listQuery.isPlaceholderData && page > lastPage) setPage(lastPage)
  const failed =
    listQuery.isError || (listQuery.data !== undefined && listQuery.data.status !== 200)

  return (
    <>
      <PageHeader
        title="Ankiety"
        actions={
          <Button
            size="sm"
            disabled={!canAuthor}
            onClick={() => void navigate({ to: '/zadania/ankiety/nowe' })}
          >
            <Plus data-icon="inline-start" /> Nowa ankieta
          </Button>
        }
      />
      <ActivationTabs active="ankiety" />
      <div className="p-6">
        {!canView ? (
          <p className="text-sm text-muted-foreground">
            Serwer nie udostępnił capabilities umożliwiających wyświetlenie ankiet.
          </p>
        ) : failed ? (
          <Empty>
            <EmptyHeader>
              <CircleAlert />
              <EmptyTitle>Nie udało się pobrać ankiet</EmptyTitle>
              <EmptyDescription>Spróbuj ponownie.</EmptyDescription>
            </EmptyHeader>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void listQuery.refetch()}
            >
              <RefreshCw data-icon="inline-start" /> Spróbuj ponownie
            </Button>
          </Empty>
        ) : listQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Ładowanie…</p>
        ) : polls.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Brak ankiet</EmptyTitle>
              <EmptyDescription>Utwórz neutralną ankietę dla uczniów.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Stan</TableHead>
                  <TableHead>Pytania</TableHead>
                  <TableHead className="text-right">Urządzenia zgodne</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {polls.map((poll) => (
                  <TableRow key={poll.id}>
                    <TableCell>
                      <Link
                        to="/zadania/ankiety/$uid"
                        params={{ uid: poll.id }}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {poll.name}
                      </Link>
                    </TableCell>
                    <TableCell>{poll.state ?? 'draft'}</TableCell>
                    <TableCell className="tabular-nums">{poll.questions.length}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {poll.distribution.eligible_device_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              count={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              className="border-t px-4 py-3"
            />
          </div>
        )}
      </div>
    </>
  )
}
